import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | undefined} */
  #pendingAddTimeout;

  /** @type {boolean} */
  #pendingAdd = false;

  /** @type {boolean} */
  #variantUpdatePending = false;

  /** @type {number | undefined} */
  #variantUpdateTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {HTMLElement | null} */
  #sectionElement = null;

  #observedAddToCartButton = null;

  #productFormObserver = new MutationObserver(() => {
    const target = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (target !== this.#observedAddToCartButton) this.#handlePageShow();
  });

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#sectionElement = this.closest('.shopify-section');

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener('blankabric:cart-updated', this.#handleBlankabricCartUpdate, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    window.addEventListener('resize', this.#handlePageShow, { signal });
    document.addEventListener('DOMContentLoaded', this.#handlePageShow, { once: true, signal });

    if (this.#sectionElement) {
      this.#productFormObserver.observe(this.#sectionElement, { childList: true, subtree: true });
    }

    this.#getInitialQuantity();
    this.#refreshPurchaseState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#setOperationalState(null);
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#productFormObserver.disconnect();
    this.#observedAddToCartButton = null;
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#pendingAddTimeout) clearTimeout(this.#pendingAddTimeout);
    if (this.#variantUpdateTimeout) clearTimeout(this.#variantUpdateTimeout);
    this.refs.addToCartButton?.removeAttribute('data-added');
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   * @returns {boolean} Whether the sticky purchase control has a valid native target
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return false;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return false;

    const targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!(targetAddToCartButton instanceof HTMLButtonElement)) return false;

    this.#targetAddToCartButton = targetAddToCartButton;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this.#isStuck) {
            this.#hiddenByBottom = true;
            this.#hideStickyBar();
          } else if (!entry.isIntersecting && this.#hiddenByBottom) {
            // Footer out of view - check if we should show sticky bar again
            const rect = buyButtonsBlock.getBoundingClientRect();
            // Only show if buy buttons are above the viewport (scrolled past)
            if (rect.bottom < 0 || rect.top < 0) {
              this.#hiddenByBottom = false;
              this.#showStickyBar();
            }
          }
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    return true;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (this.#variantUpdatePending) return;

    const productForm = this.#getProductForm();
    const targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]');
    if (!this.#setOperationalState(targetAddToCartButton)) return;

    // The visible mobile quantity is the source of truth at the instant of the
    // click (notably after a reload/BFCache restore). Copy it to Shopify's native
    // form before delegating the submission.
    this.#syncQuantityFromForm(true);

    this.#clearSuccessState();
    this.#pendingAdd = true;
    // A network failure outside Shopify's normal cart:error path must never
    // leave either the puppet flag or the success state stuck indefinitely.
    if (this.#pendingAddTimeout) clearTimeout(this.#pendingAddTimeout);
    this.#pendingAddTimeout = setTimeout(() => {
      this.#pendingAdd = false;
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
      this.#clearSuccessState();
    }, 12000);

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    this.#variantUpdatePending = false;
    if (this.#variantUpdateTimeout) clearTimeout(this.#variantUpdateTimeout);
    this.#variantUpdateTimeout = undefined;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
    this.refs.addToCartButton?.removeAttribute('data-bb-default-aria-label');

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    this.#setOperationalState(productForm?.querySelector('[ref="addToCartButton"]'));
    queueMicrotask(() => {
      const updatedProductForm = this.#getProductForm();
      this.#setOperationalState(updatedProductForm?.querySelector('[ref="addToCartButton"]'));
      this.#syncQuantityFromForm(false);
    });

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    if (!event.detail.resource?.id) return;

    // `variant:selected` carries an option-value id, not a purchasable variant id.
    // Keep the sticky bar mounted while Shopify fetches the validated variant so
    // it can update in place without flashing out of view. Block the very short
    // transition window to avoid submitting the previously selected variant.
    this.#variantUpdatePending = true;
    if (this.#variantUpdateTimeout) clearTimeout(this.#variantUpdateTimeout);
    this.#variantUpdateTimeout = setTimeout(() => {
      this.#variantUpdatePending = false;
      this.#variantUpdateTimeout = undefined;
    }, 2000);
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles the delegated native form result. Only this pending sticky action
   * is allowed to animate this button.
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    if (!this.#pendingAdd) return;

    const productForm = this.#getProductForm();
    const data = event.detail?.data || {};
    const isOwnError = event.type === ThemeEvents.cartError && event.target === productForm;
    const isOwnSuccess =
      event.type === ThemeEvents.cartUpdate &&
      data.source === 'product-form-component' &&
      String(data.productId || '') === String(this.dataset.productId || '');
    if (!isOwnError && !isOwnSuccess) return;

    this.#pendingAdd = false;
    if (this.#pendingAddTimeout) clearTimeout(this.#pendingAddTimeout);
    this.#pendingAddTimeout = undefined;

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    if (isOwnError || data.didError) {
      this.#clearSuccessState();
      return;
    }

    // ProductForm has refreshed the inventory/cart constraint before emitting
    // cart:update. Reflect any resulting clamp immediately in the sticky UI.
    this.#syncQuantityFromForm(false);
    this.#showTransientSuccess();
  };

  #handlePageShow = () => {
    requestAnimationFrame(() => {
      if (this.isConnected) this.#refreshPurchaseState();
    });
  };

  #refreshPurchaseState() {
    const target = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (target !== this.#observedAddToCartButton) {
      this.#observedAddToCartButton = target;
      this.#buyButtonsIntersectionObserver?.disconnect();
      this.#mainBottomObserver?.disconnect();
      this.#setupIntersectionObserver();
    }
    this.#setOperationalState(target);
    this.#syncQuantityFromForm(false);
  }

  #handleBlankabricCartUpdate = () => {
    queueMicrotask(() => this.#syncQuantityFromForm(false));
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#syncQuantityFromForm(false);
  };

  /**
   * Reconciles the sticky quantity and the real Shopify form quantity.
   * @param {boolean} preferVisible Whether the currently displayed sticky value wins
   * @returns {number}
   */
  #syncQuantityFromForm(preferVisible) {
    const productForm = this.#getProductForm();
    const input = productForm?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return this.#currentQuantity;

    const visibleQuantity = Number.parseInt(this.refs.quantityNumber?.textContent || '', 10);
    const inputQuantity = Number.parseInt(input.value || '', 10);
    const minimum = Number.parseInt(input.min || input.dataset.min || '1', 10) || 1;
    const step = Number.parseInt(input.step || '1', 10) || 1;
    const absoluteMaximum = input.max ? Number.parseInt(input.max, 10) : null;
    const cartQuantity = Number.parseInt(input.dataset.cartQuantity || '0', 10) || 0;
    const effectiveMaximum = Number.isFinite(absoluteMaximum)
      ? Math.max(Number(absoluteMaximum) - cartQuantity, minimum)
      : null;

    const preferredQuantity = preferVisible ? visibleQuantity || inputQuantity : inputQuantity || visibleQuantity;
    let quantity = Number.isFinite(preferredQuantity) && preferredQuantity > 0 ? preferredQuantity : minimum;
    quantity = minimum + Math.floor(Math.max(0, quantity - minimum) / step) * step;
    quantity = Math.max(minimum, effectiveMaximum === null ? quantity : Math.min(effectiveMaximum, quantity));

    input.value = String(quantity);
    this.#currentQuantity = quantity;
    this.#setCartLimitState(
      Number.isFinite(absoluteMaximum) && cartQuantity + quantity > Number(absoluteMaximum)
    );
    this.#updateButtonText();

    // Keep all native price, inventory and validation listeners in sync.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    return quantity;
  }

  /**
   * Shows a clear disabled state when the selected variant's remaining stock is exhausted.
   * @param {boolean} limitReached
   */
  #setCartLimitState(limitReached) {
    const addToCartButton = this.refs.addToCartButton;
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    const isAvailable = this.dataset.variantAvailable === 'true';
    const isLimitReached = isAvailable && limitReached;
    this.dataset.bbCartLimitReached = isLimitReached ? 'true' : 'false';

    const limitLabel = addToCartButton.querySelector('.bb-sticky-purchase__limit-label')?.textContent?.trim() || '';
    if (!addToCartButton.dataset.bbDefaultAriaLabel) {
      addToCartButton.dataset.bbDefaultAriaLabel = addToCartButton.getAttribute('aria-label') || '';
    }

    if (isLimitReached) {
      addToCartButton.disabled = true;
      if (limitLabel) addToCartButton.setAttribute('aria-label', limitLabel);
    } else {
      addToCartButton.disabled = !isAvailable;
      if (addToCartButton.dataset.bbDefaultAriaLabel) {
        addToCartButton.setAttribute('aria-label', addToCartButton.dataset.bbDefaultAriaLabel);
      }
    }
  }

  #clearSuccessState() {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = undefined;
    this.refs.addToCartButton?.removeAttribute('data-added');
  }

  #showTransientSuccess() {
    this.#clearSuccessState();
    this.refs.addToCartButton.dataset.added = 'true';
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
      this.#resetTimeout = undefined;
    }, 1200);
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Keeps the native mobile form visible until the sticky bar can delegate to it safely.
   * @param {Element | null | undefined} targetAddToCartButton
   * @returns {boolean}
   */
  #setOperationalState(targetAddToCartButton) {
    const productForm = targetAddToCartButton?.closest('product-form-component');
    const variantInput = productForm?.querySelector('input[name="id"]');
    const requiresVariantId = this.dataset.variantAvailable === 'true';
    const selectionInvalid = productForm?.hasAttribute('data-variant-selection-invalid') || false;

    if (requiresVariantId && !selectionInvalid && variantInput && !variantInput.value) {
      const picker = this.#sectionElement?.querySelector(
        `variant-picker[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`
      );
      const interactiveControls = picker?.querySelectorAll('fieldset input:checked, select') || [];
      let fallbackVariantId = '';

      if (picker && interactiveControls.length) {
        const selectedValues = Array.from(interactiveControls).map((control) => {
          if (control instanceof HTMLSelectElement) return control.options[control.selectedIndex]?.value || '';
          return control instanceof HTMLInputElement ? control.value : '';
        });
        try {
          const selectedVariant = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
          const selectionMatches =
            selectedVariant?.available === true &&
            /^\d+$/.test(String(selectedVariant.id || '')) &&
            Array.isArray(selectedVariant.options) &&
            selectedVariant.options.length === selectedValues.length &&
            selectedVariant.options.every((option, index) => String(option) === selectedValues[index]);
          if (selectionMatches) fallbackVariantId = String(selectedVariant.id);
        } catch (_error) {
          fallbackVariantId = '';
        }
      } else {
        const renderedVariantId = productForm?.dataset.currentVariantId || this.dataset.currentVariantId || '';
        if (/^\d+$/.test(renderedVariantId)) fallbackVariantId = renderedVariantId;
      }

      if (fallbackVariantId) {
        variantInput.value = fallbackVariantId;
        variantInput.setAttribute('value', fallbackVariantId);
        if (productForm instanceof HTMLElement) productForm.dataset.currentVariantId = fallbackVariantId;
      }
    }

    const isOperational =
      targetAddToCartButton instanceof HTMLButtonElement &&
      targetAddToCartButton.isConnected &&
      (!requiresVariantId || (!selectionInvalid && Boolean(variantInput?.value)));

    this.#targetAddToCartButton = isOperational ? targetAddToCartButton : null;
    this.toggleAttribute('data-bb-purchase-operational', isOperational);

    const sectionElement = this.#sectionElement;
    if (isOperational) {
      sectionElement?.classList.add('bb-sticky-purchase-ready');
    } else if (!sectionElement?.querySelector('sticky-add-to-cart[data-bb-purchase-operational]')) {
      sectionElement?.classList.remove('bb-sticky-purchase-ready');
    }

    return isOperational;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    const input = this.#getProductForm()?.querySelector('input[name="quantity"]');
    this.#currentQuantity =
      (input instanceof HTMLInputElement ? Number.parseInt(input.value || '', 10) : 0) ||
      Number.parseInt(this.dataset.initialQuantity || '1', 10) ||
      1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
